import { useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import { config } from '../../../utils/config';
import { Country } from '../../../utils/countryData';
import loadCdnData from '../../../utils/loadCdnData';

type ZipCodeCityMapping = Record<string, string>;
type ZipCodeCityMappings = Partial<Record<Country, ZipCodeCityMapping>>;
const cachedZipCodeCityMappings: ZipCodeCityMappings = {};
const loadZipCodeCityMapping = async (country: Country) =>
  (cachedZipCodeCityMappings[country] = await loadCdnData<ZipCodeCityMapping>(
    `zip-code-city-mappings/${country}.json`
  ));

type CityZipCodeMapping = Record<string, string[]>;
type CityZipCodeMappings = Partial<Record<Country, CityZipCodeMapping>>;
const cachedCityZipCodeMappings: CityZipCodeMappings = {};
const loadCityZipCodeMapping = async (country: Country) =>
  (cachedCityZipCodeMappings[country] = await loadCdnData<CityZipCodeMapping>(
    `city-zip-code-mappings/${country}.json`
  ));

const normalizeCity = (city: string) =>
  city
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\W\d_]/g, '');

const useAddressCompletion = ({ context, country }: { context?: string; country: Country }) => {
  const { getFieldMeta, setFieldValue } = useFormikContext<any>();
  const zipCodeFieldName = [context, 'zipCode'].filter(Boolean).join('.');
  const { value: zipCodeValue, error: zipCodeError } = getFieldMeta<string>(zipCodeFieldName);
  const cityFieldName = [context, 'city'].filter(Boolean).join('.');
  const { value: cityValue, error: cityError } = getFieldMeta<string>(cityFieldName);
  const [suggestedZipCodes, setSuggestedZipCodes] = useState<string[]>();

  useEffect(() => {
    const zipCodeCityConfig = config.addressForms[country].zipCodeCity;
    const isZipCodeCompletionEnabled = zipCodeCityConfig.zipCode.completion?.enabled;
    if (isZipCodeCompletionEnabled && !(country in cachedCityZipCodeMappings)) {
      loadCityZipCodeMapping(country);
    }
    const isCityCompletionEnabled = zipCodeCityConfig.city?.completion?.enabled;
    if (isCityCompletionEnabled && !(country in cachedZipCodeCityMappings)) {
      loadZipCodeCityMapping(country);
    }
  }, [country]);

  const getZipCodes = (_cityValue: string) => cachedCityZipCodeMappings[country]?.[normalizeCity(_cityValue)];

  const onCityBlur = () => {
    if (!(cityValue && !cityError)) {
      return;
    }
    const _suggestedZipCodes = getZipCodes(cityValue);
    setSuggestedZipCodes(_suggestedZipCodes);
    if (_suggestedZipCodes?.length === 1) {
      setFieldValue(zipCodeFieldName, _suggestedZipCodes[0]);
    }
  };

  const getCity = (_zipCodeValue: string) => cachedZipCodeCityMappings[country]?.[_zipCodeValue.replaceAll(' ', '')];

  const onZipCodeBlur = () => {
    if (!(zipCodeValue && !zipCodeError)) {
      return;
    }

    const suggestedCity = getCity(zipCodeValue);
    if (suggestedCity) {
      setFieldValue(cityFieldName, suggestedCity);
    }
  };

  return { onZipCodeBlur, onCityBlur, suggestedZipCodes };
};

export default useAddressCompletion;
